import type { Booking, SlotData } from "../../hooks/bookingTypes";
import css from "./Slot.module.css";
import { autoBlockNote, blockReasonOf } from "../modal/blockInfo";

/**
 * One slot cell in the DC booking grid.
 *
 * Visual taxonomy (A5 in DECISIONS-MASTER):
 *
 *  base state           colour (border)  style       extra
 *  ─────────────────────────────────────────────────────────────
 *  free                 —                text-only   surface-2 fill
 *  fresh lead           purple           solid       purple-tint text
 *  processed lead       green            dashed      green-tint text
 *  unpaid booking       orange           solid       text-1
 *  paid booking         green            solid       text-1
 *  block                blue             solid       blue-tint fill
 *  auto-block           cyan             transparent muted, opacity .55
 *  conflict             red              solid       pink italic time
 *
 * Modifiers (overlays — not exclusive):
 *  tea           inset blue dashed border
 *  МирКв (A14)   inset white-tint border
 *
 * Cell semantics: ONLY border + time text. No glyphs / dots / icons inside
 * the cell — state is read through the frame (B.2.4).
 */

export interface SlotProps {
  questId: string;
  /** Time displayed in the cell (may differ from base for "shifting" branches). */
  displayTime: string;
  /** Base slot time used as data key. */
  baseTime: string;
  /** Booking / auto-block at this slot, if any. */
  data: SlotData | null;
  /** If the slot has both a booking AND a lead, this is the conflicting lead. */
  conflictLead: Booking | null;
  /** Move-mode highlight — cell glows purple to indicate it's a drop target. */
  moveTarget: boolean;
  /** Selected/focused (after keyboard nav or pending action). */
  focused?: boolean;
  /** Booking just appeared (live update) — plays an entrance animation. */
  isNew?: boolean;
  /** 17.09: игру сдвигает мастер-класс (04.10: или лишняя чайная), либо блок «Чайная» стал лишним — текст метки. */
  clash?: string | null;
  /** 17.09: свободный слот сдвинут (МК; 04.10: чайной назад и вперёд) — новая бронь сразу на это время. */
  presetTime?: string | null;
  onClick: (questId: string, baseTime: string, data: SlotData | null, conflict: Booking | null, presetTime?: string | null, clash?: string | null) => void;
}

export function Slot({
  questId,
  displayTime,
  baseTime,
  data,
  conflictLead,
  moveTarget,
  focused,
  isNew,
  clash,
  presetTime,
  onClick,
}: SlotProps) {
  const status = computeStatus(data, conflictLead);
  const bk = data && data.type !== "auto-block" ? (data as Booking) : null;
  /* 17.09: игра на слоте, сдвинутом мастер-классом (04.10: или чайной), — подсказка, куда её перенести */
  const hint = clash
    ? clash
    : data?.type === "auto-block" ? autoBlockNote(data)   // 12.10: своя уборка ≠ связанный квест
    : bk?.type === "block" ? `Блок${blockReasonOf(bk) ? `: ${blockReasonOf(bk)}` : ""}` : undefined;   // 28.09: зачем заблокировали
  const tea = !!bk?.tea;
  /* Custom time shows unconditionally when persisted (V1 canon `ct || displayTime`,
   * v1-v3/index.html:1408) — V1-form rows keep time = standard slot, so the old
   * `time !== baseTime` check missed them; it stays as fallback for re-keyed
   * bookings without custom_time. Shifting-branch slots keep time===baseTime. */
  const custom = bk ? (bk.customTime || (bk.time !== baseTime ? bk.time : null)) : null;
  const shownTime = custom ?? displayTime;

  const classes = [
    css.slot,
    css[`s_${status}`],
    tea && css.tea,
    custom && css.custom,
    clash && css.mcClash,
    isNew && css.isNew,
    moveTarget && css.moveTarget,
    focused && css.focus,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={classes}
      title={hint}
      onClick={() => onClick(questId, baseTime, data, conflictLead, presetTime, clash)}
      data-quest={questId}
      data-time={baseTime}
    >
      <span className={css.time}>{shownTime}</span>
    </button>
  );
}

type SlotStatus =
  | "free"
  | "lead"
  | "leadProc"
  | "unpaid"
  | "paid"
  | "block"
  | "autoBlock"
  | "conflict";

function computeStatus(data: SlotData | null, conflict: Booking | null): SlotStatus {
  if (conflict) return "conflict";
  if (!data) return "free";
  if (data.type === "auto-block") return "autoBlock";
  if (data.type === "block") return "block";
  if (data.type === "lead") {
    const b = data as Booking;
    /* Lead with prepay processed = green dashed (B.2.4 obsolete pattern but kept) */
    return b.prepaid ? "leadProc" : "lead";
  }
  /* booking */
  const b = data as Booking;
  return b.prepaid ? "paid" : "unpaid";
}
